import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Archive, Calendar, Database, FileText, Shield, Target, Timer, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { Card, CardContent } from '@/components/ui/card';
import type { CitationChipData } from '@/components/ui/citation-chip';
import type { Origin } from '@/components/ui/provenance-chip';
import { SowVersionPinner } from '@/components/contract/sow-version-pinner';
import { BaselineSection, type BaselineItem } from '@/components/contract/baseline-section';
import { ContractHeader } from '@/components/contract/contract-header';
import { ContractAttentionPanel } from '@/components/contract/attention-panel';
import { ContractFactsCard } from '@/components/contract/contract-facts-card';
import {
  DeliverableDrawer,
  type DeliverableDrawerMode,
} from '@/components/contract/deliverable-drawer';
import { compareDeliverables, statusWord } from '@/components/contract/deliverable-status';
import { ReevalTrayModal } from '@/components/modals/reeval-tray-modal';
import { VerifyStateModal } from '@/components/modals/verify-state-modal';
import { useChangeOrderChainQuery } from '@/lib/api/change-orders';
import { useContractFactsQuery } from '@/lib/api/contract-facts';
import { formatDate } from '@/lib/utils/format';
import { useAiReviewPolicyQuery } from '@/lib/api/ai-review-policy';
import { useProposalQueueQuery, type RoutingProposal } from '@/lib/api/review-proposals';
import {
  isIvvGate,
  useEdlListQuery,
  useMoveEdlItemMutation,
  type EdlEntry,
} from '@/lib/api/deliverables';
import {
  backendOriginToProvenance,
  useRegisterListQuery,
  type RegisterEntry,
} from '@/lib/api/registers';

/**
 * Contract → Baseline (C20-133 / T-0102 / E06-F01), the body of the
 * `/contract/baseline` route.
 *
 * Visual contract: `screens-modules-tabs.js → contractBaseline()` (lines
 * 66-424): header, SOW version pinner, the review-status banner, the
 * inventory tiles, then one card-list per baseline-forming category.
 *
 * Production issue 10 (what the banner counts). The banner used to count
 * each row's REGISTER status, so an approved deliverable that the vendor had
 * not delivered yet ("outstanding") was shown as pending acceptance. Now:
 *
 *   - approved + noted — every committed baseline entry (a register row,
 *     an EDL row, a contract fact with a value): each one exists because a
 *     review decision approved it, or the firm's auto-approve did.
 *   - pending / revise / rejected — the review decisions still open or
 *     negative on baseline-forming proposals (from the review queue).
 *   - vendor submissions — the deliverables' submittal lifecycle
 *     (outstanding / submitted / under review / ...), on its own line.
 *
 * Rows carry their real provenance (`backendOriginToProvenance` of the
 * entry's origin), and deliverables are listed in contract order
 * (`compareDeliverables`: sow_ref, source position, title — the same order
 * as the Deliverables page, so D-codes agree; production issue 12).
 *
 * IV&V gates (E19 / T-0218). A milestone the vendor contract names as an
 * IV&V checkpoint carries the "IV&V gate" chip. Its Edit opens the
 * deliverable drawer's edit form (the EDL PATCH path, with the structured
 * override for AI-extracted rows), where a lead toggles the flag.
 */
export interface ContractBaselineScreenProps {
  engagementId: string;
}

const BASELINE_REGISTER_TYPE = 'contract_compliance_baseline';

/** Proposal types that form the contract baseline, whatever flagged them. */
const BASELINE_PROPOSAL_TYPES: ReadonlySet<string> = new Set(['edl_item', 'contract_fact']);

export function ContractBaselineScreen({ engagementId }: ContractBaselineScreenProps) {
  const [activeVersion, setActiveVersion] = useState<'current' | 'original'>('current');
  // The deliverable drawer, opened in edit mode from an EDL milestone row.
  const [drawer, setDrawer] = useState<DeliverableDrawerMode | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<EdlEntry | null>(null);
  const [tray, setTray] = useState<{ overrideId: string; reasonTag: string } | null>(null);

  // EDL — drives the deliverables tile + the version-pinner deliverable count.
  const edl = useEdlListQuery(engagementId);
  // The contract version in force: the newest registered change order, or
  // the baseline SOW when there are none.
  const chain = useChangeOrderChainQuery(engagementId).data;
  const currentSow = chain?.change_orders.at(-1) ?? chain?.baseline ?? null;

  // Register-entries fetch for milestones / payment terms / KPIs / SLAs.
  const baselineItems = useRegisterListQuery(engagementId, BASELINE_REGISTER_TYPE, {
    include_deleted: false,
  });
  const facts = useContractFactsQuery(engagementId).data;
  const autoApproveOn = useAiReviewPolicyQuery().data?.auto_approve_proposals === true;
  const queue = useProposalQueueQuery(engagementId, { limit: 200 }).data;

  const grouped = useMemo(() => {
    const entries = baselineItems.data?.entries ?? [];
    return {
      milestone: entries.filter((e) => payloadKind(e.payload) === 'milestone'),
      payment_term: entries.filter((e) => payloadKind(e.payload) === 'payment_term'),
      kpi: entries.filter((e) => payloadKind(e.payload) === 'kpi'),
      sla: entries.filter((e) => payloadKind(e.payload) === 'sla'),
    };
  }, [baselineItems.data]);

  // AI-committed EDL rows carry item_type (deliverable | milestone |
  // payment_term | kpi | sla | integration) — bucket them into the same
  // sections as the legacy `kind`-tagged baseline rows so both vocabularies
  // land in one place. Every bucket is in contract order.
  const edlByType = useMemo(() => {
    const entries = [...(edl.data?.entries ?? [])].sort(compareDeliverables);
    const buckets: Record<string, typeof entries> = {
      deliverable: [],
      milestone: [],
      payment_term: [],
      kpi: [],
      sla: [],
      integration: [],
    };
    for (const e of entries) {
      const t = e.item_type && buckets[e.item_type] ? e.item_type : 'deliverable';
      buckets[t]!.push(e);
    }
    return buckets;
  }, [edl.data]);

  const edlTypeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const [t, list] of Object.entries(edlByType)) {
      for (const e of list) m.set(e.register_entry_id, t);
    }
    return m;
  }, [edlByType]);

  const renderMoveAction = (item: BaselineItem) =>
    item.movable ? (
      <MoveEdlMenu
        registerEntryId={item.id}
        current={edlTypeById.get(item.id) ?? 'deliverable'}
        engagementId={engagementId}
      />
    ) : null;

  // C2-18: a milestone is submitted, accepted or sent back on the
  // Deliverables tab like any deliverable; its Baseline row links there.
  const renderMilestoneActions = (item: BaselineItem) => (
    <>
      {renderMoveAction(item)}
      {edlTypeById.get(item.id) === 'milestone' ? (
        <Button asChild variant="outline" size="sm">
          <Link
            to="/engagements/$engagementId/contract/deliverables"
            params={{ engagementId }}
            search={{ open: item.id }}
          >
            Track
          </Link>
        </Button>
      ) : null}
    </>
  );

  const deliverableCount = edlByType.deliverable!.length;
  const milestoneCount = grouped.milestone.length + edlByType.milestone!.length;
  const paymentCount = grouped.payment_term.length + edlByType.payment_term!.length;
  const kpiCount = grouped.kpi.length + edlByType.kpi!.length;
  const slaCount = grouped.sla.length + edlByType.sla!.length;
  const integrationCount = edlByType.integration!.length;
  const totalItems =
    deliverableCount + milestoneCount + paymentCount + kpiCount + slaCount + integrationCount;

  const reviewCounts = useMemo(
    () =>
      baselineReviewCounts({
        registerEntries: baselineItems.data?.entries ?? [],
        edlEntries: edl.data?.entries ?? [],
        facts: facts?.facts ?? [],
        proposals: queue?.items ?? [],
      }),
    [baselineItems.data, edl.data, facts, queue],
  );
  const vendorCounts = useMemo(() => vendorSubmissionCounts(edlByType.deliverable!), [edlByType]);

  const isLoading = edl.isLoading || baselineItems.isLoading;
  const error = edl.error ?? baselineItems.error;

  return (
    <div className="space-y-6">
      <ContractHeader
        engagementId={engagementId}
        active="baseline"
        meta={
          <span>
            {totalItems} baseline-forming {totalItems === 1 ? 'item' : 'items'} · {deliverableCount}{' '}
            {deliverableCount === 1 ? 'deliverable' : 'deliverables'} tracked
          </span>
        }
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/engagements/$engagementId/contract/changes" params={{ engagementId }}>
                <Archive className="h-4 w-4" aria-hidden="true" />
                SOW history
              </Link>
            </Button>
            <Button
              asChild
              size="sm"
              className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
            >
              <Link to="/engagements/$engagementId/proposals" params={{ engagementId }}>
                <Zap className="h-4 w-4" aria-hidden="true" />
                Open review queue
              </Link>
            </Button>
          </>
        }
      />

      <ContractAttentionPanel engagementId={engagementId} />

      <ContractFactsCard engagementId={engagementId} />

      <SowVersionPinner
        versionLabel={
          activeVersion === 'current' ? `v${currentSow?.version_seq ?? 0}` : 'v0 (original)'
        }
        effectiveDate={currentSow?.effective_date ?? 'not yet registered'}
        deliverableCount={deliverableCount}
        versionsAvailable={1}
        active={activeVersion}
        onChange={setActiveVersion}
        title={activeVersion === 'current' ? 'Active SOW baseline' : 'Original SOW baseline'}
      />

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-body-sm text-ink-500">
            <Spinner /> Loading baseline…
          </CardContent>
        </Card>
      ) : error ? (
        <ErrorMessage error={error} />
      ) : totalItems === 0 ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title="No baseline registered yet"
              description="Register a SOW from the Documents tab to extract milestones, payment terms, KPIs, SLAs, and deliverables. AI proposals will appear here for consultant review."
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <ReviewStatusBanner
            counts={reviewCounts}
            vendor={vendorCounts}
            autoApproveOn={autoApproveOn}
          />

          <InventoryStrip
            engagementId={engagementId}
            deliverables={deliverableCount}
            milestones={milestoneCount}
            payments={paymentCount}
            kpis={kpiCount}
            slas={slaCount}
            integrations={integrationCount}
          />

          <div className="space-y-4">
            <BaselineSection
              title={`Milestones · ${milestoneCount}`}
              description="Schedule gates with payment triggers."
              icon={Calendar}
              accent="border-l-accent-amber-600"
              items={[
                ...grouped.milestone.map((e) => registerEntryToItem(e, 'M')),
                ...edlByType.milestone!.map((e) => ({
                  ...edlEntryToItem(e, 'M'),
                  onEdit: () => setDrawer({ mode: 'edit', entryId: e.register_entry_id }),
                })),
              ]}
              emptyMessage="No milestones extracted from the SOW yet."
              renderActions={renderMilestoneActions}
            />
            <BaselineSection
              title={`Payment terms · ${paymentCount}`}
              description="Tied to milestone acceptance events."
              icon={Database}
              accent="border-l-ink-700"
              items={[
                ...grouped.payment_term.map((e) => registerEntryToItem(e, 'P')),
                ...edlByType.payment_term!.map((e) => edlEntryToItem(e, 'P')),
              ]}
              emptyMessage="No payment terms extracted from the SOW yet."
              renderActions={renderMoveAction}
            />
            <BaselineSection
              title={`KPIs · ${kpiCount}`}
              description="Quantitative targets the engagement is measured against."
              icon={Target}
              accent="border-l-posture-at-risk"
              items={[
                ...grouped.kpi.map((e) => registerEntryToItem(e, 'K')),
                ...edlByType.kpi!.map((e) => edlEntryToItem(e, 'K')),
              ]}
              emptyMessage="No KPIs extracted from the SOW yet."
              renderActions={renderMoveAction}
            />
            <BaselineSection
              title={`SLAs · ${slaCount}`}
              description="Response-time obligations with measurement windows."
              icon={Timer}
              accent="border-l-posture-critical"
              items={[
                ...grouped.sla.map((e) => registerEntryToItem(e, 'S')),
                ...edlByType.sla!.map((e) => edlEntryToItem(e, 'S')),
              ]}
              emptyMessage="No SLAs extracted from the SOW yet."
              renderActions={renderMoveAction}
            />
            <BaselineSection
              title={`Integrations · ${integrationCount}`}
              description="System interfaces, data feeds, and API connections in scope."
              icon={Zap}
              accent="border-l-ai-ink"
              items={edlByType.integration!.map((e) => edlEntryToItem(e, 'I'))}
              emptyMessage="No integrations extracted from the SOW yet."
              renderActions={renderMoveAction}
            />
            <DeliverablesBaselineSection
              entries={edlByType.deliverable!}
              renderActions={renderMoveAction}
            />
          </div>
        </>
      )}

      <DeliverableDrawer
        engagementId={engagementId}
        entries={edlByType.milestone!}
        state={drawer}
        onStateChange={setDrawer}
        onVerify={(e) => setVerifyTarget(e)}
        onOverrideCaptured={(overrideId, reasonTag) => setTray({ overrideId, reasonTag })}
      />
      {verifyTarget ? (
        <VerifyStateModal
          engagementId={engagementId}
          registerType="deliverable"
          entryId={verifyTarget.register_entry_id}
          subjectName={verifyTarget.title}
          subjectKind="Milestone"
          open
          onOpenChange={(o) => !o && setVerifyTarget(null)}
        />
      ) : null}
      {tray ? (
        <ReevalTrayModal
          engagementId={engagementId}
          triggerOverrideId={tray.overrideId}
          reasonTag={tray.reasonTag}
          open
          onOpenChange={(o) => !o && setTray(null)}
        />
      ) : null}
    </div>
  );
}

// ===========================================================================
// Review-status counts — pure, so they can be checked without a screen.
// ===========================================================================

export interface ReviewCounts {
  /** Committed baseline entries: approved or approved as noted. */
  approved: number;
  /** Baseline-forming proposals still waiting for a decision. */
  pending: number;
  revise: number;
  rejected: number;
}

export interface VendorSubmissionCounts {
  outstanding: number;
  submitted: number;
  under_review: number;
  accepted: number;
  revise_resubmit: number;
  rejected: number;
  overdue: number;
}

/**
 * Acceptance comes from review decisions: every committed entry was
 * approved (as is or as noted), and the open or negative decisions are the
 * baseline-forming proposals in the review queue. A deliverable's register
 * status is the vendor's submittal lifecycle, never its acceptance.
 */
export function baselineReviewCounts(input: {
  registerEntries: ReadonlyArray<Pick<RegisterEntry, 'status'>>;
  edlEntries: ReadonlyArray<unknown>;
  facts: ReadonlyArray<{ value: string | null }>;
  proposals: ReadonlyArray<Pick<RoutingProposal, 'decision' | 'is_baseline' | 'proposal_type'>>;
}): ReviewCounts {
  const counts: ReviewCounts = { approved: 0, pending: 0, revise: 0, rejected: 0 };
  counts.approved += input.registerEntries.filter((e) => e.status !== 'proposed').length;
  counts.approved += input.edlEntries.length;
  counts.approved += input.facts.filter((f) => f.value !== null && f.value !== '').length;
  for (const p of input.proposals) {
    if (!p.is_baseline && !BASELINE_PROPOSAL_TYPES.has(p.proposal_type)) continue;
    if (p.decision === 'pending') counts.pending += 1;
    else if (p.decision === 'revise') counts.revise += 1;
    else if (p.decision === 'rejected') counts.rejected += 1;
  }
  return counts;
}

/** The deliverables' submittal lifecycle, counted by register status. */
export function vendorSubmissionCounts(
  entries: ReadonlyArray<Pick<EdlEntry, 'status'>>,
): VendorSubmissionCounts {
  const counts: VendorSubmissionCounts = {
    outstanding: 0,
    submitted: 0,
    under_review: 0,
    accepted: 0,
    revise_resubmit: 0,
    rejected: 0,
    overdue: 0,
  };
  for (const e of entries) {
    switch (e.status) {
      case 'approved':
      case 'approved_as_noted':
        counts.accepted += 1;
        break;
      case 'submitted':
        counts.submitted += 1;
        break;
      case 'under_review':
        counts.under_review += 1;
        break;
      case 'revise_resubmit':
        counts.revise_resubmit += 1;
        break;
      case 'rejected':
        counts.rejected += 1;
        break;
      case 'overdue':
        counts.overdue += 1;
        break;
      default:
        counts.outstanding += 1;
    }
  }
  return counts;
}

// ===========================================================================
// Review-status banner — matches prototype `.baseline-banner` (lines 301-313)
// ===========================================================================

function ReviewStatusBanner({
  counts,
  vendor,
  autoApproveOn,
}: {
  counts: ReviewCounts;
  vendor: VendorSubmissionCounts;
  autoApproveOn: boolean;
}) {
  const vendorLine = [
    `${vendor.outstanding} outstanding`,
    `${vendor.submitted} submitted`,
    `${vendor.under_review} under review`,
    ...(vendor.accepted > 0 ? [`${vendor.accepted} accepted`] : []),
    ...(vendor.revise_resubmit > 0 ? [`${vendor.revise_resubmit} revise & resubmit`] : []),
    ...(vendor.rejected > 0 ? [`${vendor.rejected} rejected`] : []),
    ...(vendor.overdue > 0 ? [`${vendor.overdue} overdue`] : []),
  ].join(' · ');
  return (
    <Card className="border-l-[3px] border-l-accent-amber-600">
      <CardContent className="flex flex-wrap items-center gap-4 p-4">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-card bg-accent-amber-600 text-paper-0"
          aria-hidden="true"
        >
          <Shield className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-body-sm font-medium text-ink-900">Baseline review status</div>
          <div className="text-meta text-ink-500">
            {autoApproveOn
              ? 'Firm auto-approve is on: AI baseline items are approved as they arrive.'
              : 'No baseline item auto-accepts — even at 0.99 confidence.'}
          </div>
          <div className="mt-1 text-meta text-ink-500" data-testid="vendor-submissions">
            Vendor submissions: {vendorLine}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-meta">
          <span className="flex items-baseline gap-1.5">
            <strong className="text-h3 font-semibold tabular-nums text-posture-healthy">
              {counts.approved}
            </strong>
            <span className="text-ink-500">approved + noted</span>
          </span>
          <span className="flex items-baseline gap-1.5">
            <strong className="text-h3 font-semibold tabular-nums text-ink-500">
              {counts.pending}
            </strong>
            <span className="text-ink-500">pending</span>
          </span>
          <span className="flex items-baseline gap-1.5">
            <strong className="text-h3 font-semibold tabular-nums text-accent-amber-700">
              {counts.revise}
            </strong>
            <span className="text-ink-500">revise &amp; resubmit</span>
          </span>
          <span className="flex items-baseline gap-1.5">
            <strong className="text-h3 font-semibold tabular-nums text-posture-critical">
              {counts.rejected}
            </strong>
            <span className="text-ink-500">rejected</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Inventory strip — matches prototype five-tile row (lines 316-322)
// ===========================================================================

function InventoryStrip({
  engagementId,
  deliverables,
  milestones,
  payments,
  kpis,
  slas,
  integrations,
}: {
  engagementId: string;
  deliverables: number;
  milestones: number;
  payments: number;
  kpis: number;
  slas: number;
  integrations: number;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
      <InventoryTile
        label="Deliverables"
        value={deliverables}
        accent="border-l-posture-healthy"
        href={`/engagements/${engagementId}/contract/deliverables`}
      />
      <InventoryTile label="Milestones" value={milestones} accent="border-l-accent-amber-600" />
      <InventoryTile label="Payment terms" value={payments} accent="border-l-ink-700" />
      <InventoryTile label="KPIs" value={kpis} accent="border-l-posture-at-risk" />
      <InventoryTile label="SLAs" value={slas} accent="border-l-posture-critical" />
      <InventoryTile label="Integrations" value={integrations} accent="border-l-ai-ink" />
    </div>
  );
}

function InventoryTile({
  label,
  value,
  accent,
  href,
}: {
  label: string;
  value: number;
  accent: string;
  href?: string;
}) {
  const inner = (
    <CardContent className="space-y-2 p-4">
      <div className="text-meta uppercase tracking-wide text-ink-500">{label}</div>
      <div className="text-h2 font-semibold tabular-nums text-ink-900">{value}</div>
    </CardContent>
  );
  const className = `overflow-hidden border-l-[3px] ${accent}`;
  if (href) {
    return (
      <a href={href} className="block no-underline" aria-label={`Open ${label}`}>
        <Card className={`${className} cursor-pointer transition-colors hover:bg-paper-50`}>
          {inner}
        </Card>
      </a>
    );
  }
  return <Card className={className}>{inner}</Card>;
}

// ===========================================================================
// Deliverables baseline section — EDL rows, in contract order.
// ===========================================================================

function DeliverablesBaselineSection({
  entries,
  renderActions,
}: {
  entries: ReadonlyArray<EdlEntry>;
  renderActions?: (item: BaselineItem) => ReactNode;
}) {
  const items: BaselineItem[] = entries.map((e, i) => ({
    ...edlEntryToItem(e, 'D'),
    code: `D-${String(i + 1).padStart(2, '0')}`,
  }));

  return (
    <BaselineSection
      title={`Deliverables · ${items.length}`}
      description="EDL items derived from the SOW chain and tracked through submittal lifecycle."
      icon={FileText}
      accent="border-l-posture-healthy"
      items={items}
      emptyMessage="No deliverables on the EDL yet."
      renderActions={renderActions}
    />
  );
}

// ===========================================================================
// Payload + status helpers
// ===========================================================================

function payloadKind(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const v = (payload as Record<string, unknown>)['kind'];
  return typeof v === 'string' ? v : null;
}

function payloadString(payload: unknown, key: string): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const v = (payload as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : null;
}

/** How the row entered the baseline, in the provenance chip's words. */
function acceptanceWord(origin: Origin): string {
  switch (origin) {
    case 'ai_confirmed':
      return 'Approved';
    case 'consultant_edited':
      return 'Approved as noted';
    case 'ai_proposed':
      return 'Pending';
    default:
      return 'Added manually';
  }
}

/**
 * Map an EDL row into a BaselineSection row. The provenance chip shows the
 * entry's real origin; the review-state chip reads "Approved · vendor
 * outstanding" — acceptance first, the vendor's submittal status after it,
 * never the one mistaken for the other.
 */
function edlEntryToItem(entry: EdlEntry, codePrefix: string): BaselineItem {
  const origin = backendOriginToProvenance(entry.origin, entry.status);
  const cite = entry.source_citation;
  const citation: CitationChipData | null = cite
    ? {
        id: cite.citation_id,
        doc_label: shortDocLabel(cite.document_title),
        section: cite.section_path,
        page: cite.page_number,
        confidence: cite.confidence,
      }
    : null;
  return {
    id: entry.register_entry_id,
    code: `${codePrefix}-${entry.register_entry_id.slice(0, 4)}`,
    title: entry.title,
    // C2-45: a due date reads "31 Mar 2027", never the raw ISO string.
    valueLabel: entry.expected_delivery_date
      ? formatDate(entry.expected_delivery_date)
      : (entry.due_text ?? undefined),
    origin,
    aiConfidence: origin === 'manual' ? null : (cite?.confidence ?? null),
    citation,
    // C2-22: an item a change order added says which one.
    source: entry.source_change_order ? { text: `From ${entry.source_change_order.label}` } : null,
    reviewState: `${acceptanceWord(origin)} · vendor ${statusWord(entry.status).toLowerCase()}`,
    movable: true,
    registerType: 'deliverable',
    ivvGate: isIvvGate(entry),
  };
}

function shortDocLabel(title: string): string {
  return title.length > 34 ? `${title.slice(0, 32)}…` : title;
}

const EDL_MOVE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'deliverable', label: 'Deliverables' },
  { value: 'milestone', label: 'Milestones' },
  { value: 'payment_term', label: 'Payment terms' },
  { value: 'kpi', label: 'KPIs' },
  { value: 'sla', label: 'SLAs' },
  { value: 'integration', label: 'Integrations' },
];

/**
 * Per-row "Move to" menu for AI-committed EDL rows — re-buckets
 * payload.item_type via POST /contract/deliverables/:id/move. The row jumps
 * sections on success (query invalidation refetches the EDL).
 */
function MoveEdlMenu({
  registerEntryId,
  current,
  engagementId,
}: {
  registerEntryId: string;
  current: string;
  engagementId: string;
}) {
  const move = useMoveEdlItemMutation(engagementId);
  return (
    <select
      aria-label="Move to section"
      value={current}
      disabled={move.isPending}
      onChange={(e) => {
        if (e.target.value !== current) {
          move.mutate({ registerEntryId, itemType: e.target.value });
        }
      }}
      className="rounded-md border border-ink-100 bg-paper-0 px-1.5 py-1 text-meta text-ink-700"
    >
      {EDL_MOVE_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function registerEntryToItem(entry: RegisterEntry, codePrefix: string): BaselineItem {
  const title =
    payloadString(entry.payload, 'title') ??
    payloadString(entry.payload, 'name') ??
    payloadString(entry.payload, 'label') ??
    'Untitled item';
  const payloadDate = payloadString(entry.payload, 'date');
  const valueLabel =
    payloadString(entry.payload, 'amount') ??
    payloadString(entry.payload, 'target') ??
    payloadString(entry.payload, 'threshold') ??
    // C2-45: a payload date reads "31 Mar 2027", never the raw ISO string.
    (payloadDate ? formatDate(payloadDate) : null);
  const sourceText = payloadString(entry.payload, 'source');
  const aiConfidence =
    typeof entry.latest_reeval_confidence === 'number'
      ? entry.latest_reeval_confidence
      : entry.original_ai_confidence;
  const origin = backendOriginToProvenance(entry.origin, entry.status);
  const isAi = origin !== 'manual';

  return {
    id: entry.id,
    registerType: entry.register_type,
    code: `${codePrefix}-${entry.id.slice(0, 4)}`,
    title,
    valueLabel: valueLabel ?? undefined,
    origin,
    aiConfidence: isAi ? aiConfidence : null,
    citation: null,
    source: sourceText ? { text: sourceText } : null,
    reviewState: entry.status,
  };
}
